import qs from 'qs';

import { json_headers, request } from './common';

/**
 * Client for svc-media: the media asset rate card and the packages built from it.
 *
 * Every endpoint answers with the standard envelope, and these functions return it whole rather
 * than unwrapping `result`, so listings keep their `pagination` alongside their rows.
 *
 * The types below mirror svc-media's OpenAPI contract by hand. Once the service publishes
 * `@luxuryescapes/contract-svc-media` they should be replaced with imports from it.
 */
const BASE_PATH = `${window.configs.API_HOST}/api/media/v1`;

/** The advertiser types a partner can be, and that an asset may be sold to. */
const PARTNER_CATEGORIES = [
  'Non-endemic',
  'Tourism Board',
  'Hotel Group',
  'Cruise Line',
  'Airline',
  'Tour Company',
] as const;

type PartnerCategory = (typeof PARTNER_CATEGORIES)[number];

/** What an asset may be sold to: an advertiser type, or one of the two rules spanning several. */
const CATEGORY_ELIGIBILITIES = [...PARTNER_CATEGORIES, 'All categories', 'Endemic Only'] as const;

type CategoryEligibility = (typeof CATEGORY_ELIGIBILITIES)[number];

interface Envelope<T> {
  status: number;
  message: string;
  result: T;
}

interface PaginatedEnvelope<T> extends Envelope<Array<T>> {
  pagination: {
    offset: number;
    limit: number;
    total_items: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

/** A category or eligibility as an asset refers to it. */
interface MediaTaxonomy {
  id: string;
  name: string;
}

interface ReachMetric {
  key: string;
  value: number;
}

interface MediaAsset {
  id: string;
  name: string;
  description: string;
  hard_cost: number;
  rate: number;
  /** A fraction of the rate, not a percentage: 80% is 0.8. */
  margin: number;
  category: MediaTaxonomy;
  category_eligibility: MediaTaxonomy;
  reach_metrics: Array<ReachMetric>;
  created_at: string;
  updated_at: string;
}

interface CreateMediaAssetBody {
  name: string;
  description: string;
  hard_cost: number;
  rate: number;
  margin: number;
  /** Category name. Free text — the category is created the first time an asset names it. */
  category: string;
  category_eligibility: CategoryEligibility;
  reach_metrics?: Array<ReachMetric>;
}

type UpdateMediaAssetBody = Partial<CreateMediaAssetBody>;

interface ListMediaAssetsParams {
  offset?: number;
  limit?: number;
  sort_by?: 'created_at' | 'updated_at' | 'name' | 'hard_cost' | 'rate' | 'margin';
  sort_order?: 'asc' | 'desc';
  /** Free text, matched against name and description. */
  filter?: string;
  category?: string;
  category_eligibility?: CategoryEligibility;
}

/** Listings carry how much a package bundles rather than the bundle itself. */
interface MediaPackageSummary {
  id: string;
  name: string;
  partner_name: string | null;
  asset_count: number;
  created_at: string;
  updated_at: string;
}

/** The brief a package was built for. Every part is optional and may never have been recorded. */
interface MediaPackageBrief {
  partner_name: string | null;
  /** Which tier a suggestion produced this package as. Null when it was assembled by hand. */
  tier: PackageTier | null;
  partner_category: MediaTaxonomy | null;
  /** YYYY-MM-DD. */
  campaign_start: string | null;
  campaign_end: string | null;
  objectives: Array<CampaignObjective>;
}

interface MediaPackage extends MediaPackageBrief {
  id: string;
  name: string;
  assets: Array<MediaAsset>;
  created_at: string;
  updated_at: string;
}

/** The brief as it is sent. `partner_category` is a name, not an id. */
interface MediaPackageBriefBody {
  partner_name?: string;
  tier?: PackageTier;
  partner_category?: PartnerCategory;
  /** YYYY-MM-DD. `campaign_end` must not be before `campaign_start`. */
  campaign_start?: string;
  campaign_end?: string;
  objectives?: Array<CampaignObjective>;
}

interface CreateMediaPackageBody extends MediaPackageBriefBody {
  name: string;
  /** Ids of existing assets. Assets are created through their own endpoint, never here. */
  asset_ids?: Array<string>;
}

interface UpdateMediaPackageBody extends MediaPackageBriefBody {
  name?: string;
  /** Replaces the whole bundle when present. Send `[]` to empty it. */
  asset_ids?: Array<string>;
}

interface ListMediaPackagesParams {
  offset?: number;
  limit?: number;
  sort_by?: 'created_at' | 'updated_at' | 'name';
  sort_order?: 'asc' | 'desc';
  filter?: string;
}

/** The funnel stages a campaign can aim at. The rate card uses the same three words. */
const CAMPAIGN_OBJECTIVES = ['Awareness', 'Consideration', 'Conversion'] as const;

type CampaignObjective = (typeof CAMPAIGN_OBJECTIVES)[number];

interface CampaignBrief {
  objective: string;
  /** YYYY-MM-DD. Must not be after `campaign_end`. */
  campaign_start: string;
  campaign_end: string;
  /** Who the campaign is for. Not every brief says. */
  target_audience?: string;
  budget: number;
  /** Who is buying. */
  partner_name?: string;
  /**
   * Which advertiser type the partner is. Without it the model cannot tell whether an
   * 'Endemic Only' asset is open to them.
   */
  partner_category?: PartnerCategory;
  objectives?: Array<CampaignObjective>;
  /** Whether LETV assets may be suggested. svc-media includes them when this is omitted. */
  include_letv?: boolean;
}

interface SuggestedAsset {
  asset: MediaAsset;
  /** Why the model chose this asset. */
  rationale: string;
}

/** The three levels a brief is answered at, cheapest first. */
const PACKAGE_TIERS = ['base', 'mid', 'premium'] as const;

type PackageTier = (typeof PACKAGE_TIERS)[number];

/** One proposed package. Nothing is persisted — create it separately if it is wanted. */
interface SuggestedMediaPackage {
  /** The media package this suggestion was stored as. */
  id: string;
  tier: PackageTier;
  name: string;
  rationale: string;
  assets: Array<SuggestedAsset>;
  /** Totalled by svc-media from its own records, not by the model. */
  total_rate: number;
  total_hard_cost: number;
  within_budget: boolean;
}

/**
 * What OpenAI proposes for a brief: exactly three packages, one per tier, in `PACKAGE_TIERS`
 * order. The budget sits alongside them because all three answer the same brief.
 */
interface MediaPackageSuggestion {
  budget: number;
  packages: Array<SuggestedMediaPackage>;
}

interface DeletedResource {
  id: string;
}

/**
 * Every call takes one of these so a TanStack Query `queryFn`/`mutationFn` can hand its `signal`
 * straight through, and a page that unmounts stops waiting on a request it no longer needs. That
 * matters most for `suggestMediaPackage`, which can run for minutes.
 */
interface RequestOptions {
  signal?: AbortSignal;
}

/* -------------------------------------------------------------------------- assets ---------- */

const listMediaAssets = (
  params: ListMediaAssetsParams = {},
  options?: RequestOptions,
): Promise<PaginatedEnvelope<MediaAsset>> => {
  const queryParams = qs.stringify(params, { skipNulls: true });

  return request(`${BASE_PATH}/assets?${queryParams}`, { method: 'GET', signal: options?.signal });
};

const getMediaAsset = (assetId: string, options?: RequestOptions): Promise<Envelope<MediaAsset>> =>
  request(`${BASE_PATH}/assets/${assetId}`, { method: 'GET', signal: options?.signal });

const createMediaAsset = (asset: CreateMediaAssetBody, options?: RequestOptions): Promise<Envelope<MediaAsset>> =>
  request(`${BASE_PATH}/assets`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(asset),
    signal: options?.signal,
  });

const updateMediaAsset = (
  assetId: string,
  asset: UpdateMediaAssetBody,
  options?: RequestOptions,
): Promise<Envelope<MediaAsset>> =>
  request(`${BASE_PATH}/assets/${assetId}`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(asset),
    signal: options?.signal,
  });

const deleteMediaAsset = (assetId: string, options?: RequestOptions): Promise<Envelope<DeletedResource>> =>
  request(`${BASE_PATH}/assets/${assetId}`, {
    method: 'DELETE',
    headers: json_headers,
    signal: options?.signal,
  });

/** Records a reading against an asset, overwriting any earlier value for the same key. */
const recordAssetReachMetric = (
  assetId: string,
  key: string,
  value: number,
  options?: RequestOptions,
): Promise<Envelope<MediaAsset>> =>
  request(`${BASE_PATH}/assets/${assetId}/reach-metrics/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ value }),
    signal: options?.signal,
  });

const deleteAssetReachMetric = (
  assetId: string,
  key: string,
  options?: RequestOptions,
): Promise<Envelope<MediaAsset>> =>
  request(`${BASE_PATH}/assets/${assetId}/reach-metrics/${encodeURIComponent(key)}`, {
    method: 'DELETE',
    headers: json_headers,
    signal: options?.signal,
  });

/* ------------------------------------------------------------------- media packages ---------- */

const listMediaPackages = (
  params: ListMediaPackagesParams = {},
  options?: RequestOptions,
): Promise<PaginatedEnvelope<MediaPackageSummary>> => {
  const queryParams = qs.stringify(params, { skipNulls: true });

  return request(`${BASE_PATH}/media-packages?${queryParams}`, {
    method: 'GET',
    signal: options?.signal,
  });
};

const getMediaPackage = (mediaPackageId: string, options?: RequestOptions): Promise<Envelope<MediaPackage>> =>
  request(`${BASE_PATH}/media-packages/${mediaPackageId}`, {
    method: 'GET',
    signal: options?.signal,
  });

const createMediaPackage = (
  mediaPackage: CreateMediaPackageBody,
  options?: RequestOptions,
): Promise<Envelope<MediaPackage>> =>
  request(`${BASE_PATH}/media-packages`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(mediaPackage),
    signal: options?.signal,
  });

const updateMediaPackage = (
  mediaPackageId: string,
  mediaPackage: UpdateMediaPackageBody,
  options?: RequestOptions,
): Promise<Envelope<MediaPackage>> =>
  request(`${BASE_PATH}/media-packages/${mediaPackageId}`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(mediaPackage),
    signal: options?.signal,
  });

const deleteMediaPackage = (mediaPackageId: string, options?: RequestOptions): Promise<Envelope<DeletedResource>> =>
  request(`${BASE_PATH}/media-packages/${mediaPackageId}`, {
    method: 'DELETE',
    headers: json_headers,
    signal: options?.signal,
  });

/** Bundles an existing asset into a package. Idempotent. */
const addAssetToMediaPackage = (
  mediaPackageId: string,
  assetId: string,
  options?: RequestOptions,
): Promise<Envelope<MediaPackage>> =>
  request(`${BASE_PATH}/media-packages/${mediaPackageId}/assets/${assetId}`, {
    method: 'PUT',
    headers: json_headers,
    signal: options?.signal,
  });

/** Removes a bundled asset from a package, leaving the asset itself in place. */
const removeAssetFromMediaPackage = (
  mediaPackageId: string,
  assetId: string,
  options?: RequestOptions,
): Promise<Envelope<MediaPackage>> =>
  request(`${BASE_PATH}/media-packages/${mediaPackageId}/assets/${assetId}`, {
    method: 'DELETE',
    headers: json_headers,
    signal: options?.signal,
  });

/**
 * Asks svc-media to propose packages for a brief.
 *
 * It sends the brief and the whole asset catalogue to OpenAI and comes back with a base, a mid
 * and a premium option. All three are stored as media packages, so every call adds three, and
 * each returned `id` points at one. This is far slower than the other calls — give it its own loading
 * state rather than blocking a page on it, and pass a `signal` so it can be abandoned.
 */
const suggestMediaPackage = (
  brief: CampaignBrief,
  options?: RequestOptions,
): Promise<Envelope<MediaPackageSuggestion>> =>
  request(`${BASE_PATH}/media-packages/suggestions`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(brief),
    signal: options?.signal,
  });

export {
  BASE_PATH,
  CAMPAIGN_OBJECTIVES,
  CATEGORY_ELIGIBILITIES,
  CampaignBrief,
  CampaignObjective,
  CategoryEligibility,
  CreateMediaAssetBody,
  CreateMediaPackageBody,
  Envelope,
  ListMediaAssetsParams,
  ListMediaPackagesParams,
  MediaAsset,
  MediaPackage,
  MediaPackageBrief,
  MediaPackageBriefBody,
  MediaPackageSuggestion,
  MediaPackageSummary,
  MediaTaxonomy,
  PACKAGE_TIERS,
  PARTNER_CATEGORIES,
  PackageTier,
  PaginatedEnvelope,
  PartnerCategory,
  ReachMetric,
  RequestOptions,
  SuggestedAsset,
  SuggestedMediaPackage,
  UpdateMediaAssetBody,
  UpdateMediaPackageBody,
  addAssetToMediaPackage,
  createMediaAsset,
  createMediaPackage,
  deleteAssetReachMetric,
  deleteMediaAsset,
  deleteMediaPackage,
  getMediaAsset,
  getMediaPackage,
  listMediaAssets,
  listMediaPackages,
  recordAssetReachMetric,
  removeAssetFromMediaPackage,
  suggestMediaPackage,
  updateMediaAsset,
  updateMediaPackage,
};
